/**
 * Utility functions for managing chat messages in session storage
 * Messages are stored per session ID to allow multiple concurrent chats
 */

interface StoredMessage {
  id: string;
  content: string;
  role: 'user' | 'system' | 'assistant';
  timestamp: string; // ISO string format
  attachment?: {
    name: string;
    size: number;
    type: string;
  };
  isWelcome?: boolean;
}

interface ConversationCache {
  sessionId: string;
  messages: StoredMessage[];
  lastUpdated: string;
}

const CHAT_STORAGE_PREFIX = 'chat_messages_';
const ACTIVE_SESSION_KEY = 'active_chat_session';

export const chatStorageUtils = {
  /**
   * Save messages for a specific session ID
   */
  saveMessages: (sessionId: string, messages: any[]) => {
    try {
      const storageKey = CHAT_STORAGE_PREFIX + sessionId;

      // Convert messages to storable format
      const storedMessages: StoredMessage[] = messages.map(msg => ({
        id: msg.id,
        content: msg.content,
        role: msg.role,
        timestamp: msg.timestamp instanceof Date ? msg.timestamp.toISOString() : msg.timestamp,
        attachment: msg.attachment ? {
          name: msg.attachment.name,
          size: msg.attachment.size,
          type: msg.attachment.type,
        } : undefined,
        isWelcome: msg.isWelcome,
      }));

      const conversationCache: ConversationCache = {
        sessionId,
        messages: storedMessages,
        lastUpdated: new Date().toISOString(),
      };

      sessionStorage.setItem(storageKey, JSON.stringify(conversationCache));

      // Update active session
      sessionStorage.setItem(ACTIVE_SESSION_KEY, sessionId);
    } catch (error) {
      console.error('Failed to save messages to session storage:', error);
    }
  },

  /**
   * Load messages for a specific session ID
   */
  loadMessages: (sessionId: string): any[] | null => {
    try {
      const storageKey = CHAT_STORAGE_PREFIX + sessionId;
      const cached = sessionStorage.getItem(storageKey);

      if (!cached) {
        return null;
      }

      const conversationCache: ConversationCache = JSON.parse(cached);

      // Convert back to message format with Date objects
      const messages = conversationCache.messages.map(msg => ({
        id: msg.id,
        content: msg.content,
        role: msg.role,
        timestamp: new Date(msg.timestamp),
        attachment: msg.attachment ? new File([], msg.attachment.name, { type: msg.attachment.type }) : undefined,
        isWelcome: msg.isWelcome,
      }));

      return messages;
    } catch (error) {
      console.error('Failed to load messages from session storage:', error);
      return null;
    }
  },

  /**
   * Get the active session ID
   */
  getActiveSession: (): string | null => {
    return sessionStorage.getItem(ACTIVE_SESSION_KEY);
  },

  /**
   * Set the active session ID
   */
  setActiveSession: (sessionId: string) => {
    sessionStorage.setItem(ACTIVE_SESSION_KEY, sessionId);
  },

  /**
   * Clear messages for a specific session ID
   */
  clearSession: (sessionId: string) => {
    try {
      const storageKey = CHAT_STORAGE_PREFIX + sessionId;
      sessionStorage.removeItem(storageKey);

      // If this was the active session, clear it
      const activeSession = sessionStorage.getItem(ACTIVE_SESSION_KEY);
      if (activeSession === sessionId) {
        sessionStorage.removeItem(ACTIVE_SESSION_KEY);
      }
    } catch (error) {
      console.error('Failed to clear session from storage:', error);
    }
  },

  /**
   * Clear all chat sessions from storage
   */
  clearAllSessions: () => {
    try {
      // Get all keys from sessionStorage
      const keys = Object.keys(sessionStorage);

      // Remove all chat-related keys
      keys.forEach(key => {
        if (key.startsWith(CHAT_STORAGE_PREFIX)) {
          sessionStorage.removeItem(key);
        }
      });

      sessionStorage.removeItem(ACTIVE_SESSION_KEY);
    } catch (error) {
      console.error('Failed to clear all sessions from storage:', error);
    }
  },

  /**
   * Get all stored session IDs
   */
  getAllSessionIds: (): string[] => {
    try {
      const keys = Object.keys(sessionStorage);
      const sessionIds = keys
        .filter(key => key.startsWith(CHAT_STORAGE_PREFIX))
        .map(key => key.replace(CHAT_STORAGE_PREFIX, ''));

      return sessionIds;
    } catch (error) {
      console.error('Failed to get all session IDs:', error);
      return [];
    }
  },

  /**
   * Check if a session has cached messages
   */
  hasSessionCache: (sessionId: string): boolean => {
    const storageKey = CHAT_STORAGE_PREFIX + sessionId;
    return sessionStorage.getItem(storageKey) !== null;
  },
};
